import { Component, OnInit, OnDestroy } from '@angular/core';
import { SecurityAdminService } from 'src/app/services/security-admin/security-admin.service';
import { SecurityService } from 'src/app/services/security/security.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import * as alertifyjs from 'alertifyjs';

interface UserSecurity {
  id: string;
  username: string;
  email: string;
  name: string;
  lastName: string;
  profile: string[];
  twoFactorAuthenticator: {
    enabled: boolean;
    active: boolean;
    enabledAt: Date | null;
    backupCodesRemaining: number;
  };
  twoFactorSMS: {
    enabled: boolean;
    active: boolean;
    phone: string | null;
    enabledAt: Date | null;
  };
  accountLock: {
    isLocked: boolean;
    permanentlyLocked: boolean;
    lockedUntil: Date | null;
    loginAttempts: number;
    lockIncidents: number;
  };
  hasActiveSession: boolean;
  recentLogins: any[];
  isOnline: boolean;
  lastConnection: Date | null;
  trustedDevices?: {
    count: number;
    devices: any[];
  };
}

@Component({
  selector: 'app-seguridad-avanzada',
  templateUrl: './seguridad-avanzada.component.html',
  styleUrls: ['./seguridad-avanzada.component.css']
})
export class SeguridadAvanzadaComponent implements OnInit, OnDestroy {
  loading = false;
  users: UserSecurity[] = [];
  usersFiltered: UserSecurity[] = [];
  people_total: any = 0;

  // Filtros
  filters = {
    searchTerm: '',
    status2FA: 'all', // all, enabled, disabled
    accountStatus: 'all' // all, locked, active
  };

  // Paginación
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  usersPaginated: UserSecurity[] = [];

  // Estadísticas
  stats = {
    total: 0,
    with2FAAuthenticator: 0,
    with2FASMS: 0,
    locked: 0,
    activeSessions: 0
  };

  // Modal de gestión de usuario
  showManageModal = false;
  selectedUser: UserSecurity | null = null;

  // Modal de historial de logins
  showLoginHistoryModal = false;
  loginHistory: any[] = [];
  loadingHistory = false;

  // Formulario de teléfono
  phoneForm = {
    phone: ''
  };

  // Formulario de correo
  emailForm = {
    email: ''
  };

  // Popover state
  openPopoverId: string | null = null;

  // Global 2FA state
  global2FAEnabled: boolean = true;
  loadingGlobal2FA: boolean = false;
  showGlobalModal: boolean = false;

  Math = Math;

  // Trusted Devices
  showTrustedDevicesModal = false;
  trustedDevicesUser: UserSecurity | null = null;
  trustedDevicesList: any[] = [];
  loadingTrustedDevices = false;
  revokingDeviceId: string | null = null;
  revokingAll = false;

  constructor(
    private securityAdminService: SecurityAdminService,
    private securityService: SecurityService,
    public _auth: AuthService
  ) { }

  ngOnInit(): void {
    this.loadUsers();
    this.loadGlobal2FAStatus();
  }

  ngOnDestroy(): void {
    // Limpieza si es necesaria
  }

  refreshUsers(): void {
    this.loadUsers(true, true);
  }

  loadUsers = async (loading = true, reload = false) => {
    this.loading = loading;

    if (reload) {
      this.currentPage = 1;
    }

    try {
      const filter = {
        page: this.currentPage,
        limit: 100
      };

      const response = await this.securityAdminService.getAllUsersWithSecurity(filter);
      const result = response?.resp;

      if (result) {
        this.people_total = result.total;

        // Reemplazar usuarios en lugar de concatenar para evitar duplicados
        this.users = result.rows;
        this.calculateStats();
        this.applyFilters();
      }

      this.loading = false;
    } catch (error: any) {
      console.error('Error al cargar usuarios:', error);
      this.loading = false;
    }
  };

  setUserEmail = async () => {
    if (!this.selectedUser) return;

    const email = this.emailForm.email?.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email || !emailRegex.test(email)) {
      alertifyjs.error('Correo electrónico inválido.');
      return;
    }

    try {
      const response = await this.securityAdminService.setUserEmail(this.selectedUser.id, email);
      if (response?.ok) {
        alertifyjs.success(response.msg || 'Correo actualizado');
        await this.loadUsers(false, true);
        this.closeManageModal();
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al actualizar correo electrónico');
    }
  };

  calculateStats(): void {
    this.stats.total = this.users.length;
    this.stats.with2FAAuthenticator = this.users.filter(u => u.twoFactorAuthenticator.enabled).length;
    this.stats.with2FASMS = this.users.filter(u => u.twoFactorSMS.enabled).length;
    this.stats.locked = this.users.filter(u => u.accountLock.isLocked || u.accountLock.permanentlyLocked).length;
    this.stats.activeSessions = this.users.filter(u => u.hasActiveSession).length;
  }

  applyFilters(): void {
    let filtered = [...this.users];

    // Filtro de búsqueda
    if (this.filters.searchTerm) {
      const term = this.filters.searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        (u.username && u.username.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term)) ||
        (u.name && u.name.toLowerCase().includes(term)) ||
        (u.lastName && u.lastName.toLowerCase().includes(term))
      );
    }

    // Filtro de estado 2FA
    if (this.filters.status2FA !== 'all') {
      if (this.filters.status2FA === 'enabled') {
        filtered = filtered.filter(u => u.twoFactorAuthenticator.enabled || u.twoFactorSMS.enabled);
      } else {
        filtered = filtered.filter(u => !u.twoFactorAuthenticator.enabled && !u.twoFactorSMS.enabled);
      }
    }

    // Filtro de estado de cuenta
    if (this.filters.accountStatus !== 'all') {
      if (this.filters.accountStatus === 'locked') {
        filtered = filtered.filter(u => u.accountLock.isLocked || u.accountLock.permanentlyLocked);
      } else {
        filtered = filtered.filter(u => !u.accountLock.isLocked && !u.accountLock.permanentlyLocked);
      }
    }

    this.usersFiltered = filtered;
    this.totalPages = Math.ceil(this.usersFiltered.length / this.pageSize);
    this.currentPage = 1;
    this.updatePagination();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  updatePagination(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.usersPaginated = this.usersFiltered.slice(start, end);
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  hasActiveFilters(): boolean {
    return this.filters.searchTerm !== '' ||
      this.filters.status2FA !== 'all' ||
      this.filters.accountStatus !== 'all';
  }

  openManageModal(user: UserSecurity): void {
    this.selectedUser = user;
    this.phoneForm.phone = user.twoFactorSMS.phone || '';
    this.emailForm.email = user.email || '';
    this.showManageModal = true;
    this.closeAllPopovers();
  }

  closeManageModal(): void {
    this.showManageModal = false;
    this.selectedUser = null;
    this.phoneForm.phone = '';
    this.emailForm.email = '';
  }
  /**
   * Elimina el correo electrónico de un usuario
   */
  deleteUserEmail = async () => {
    if (!this.selectedUser || !this.selectedUser.id || !this.selectedUser.email) return;

    alertifyjs.confirm('¿Estás seguro de que deseas eliminar el correo electrónico de este usuario? Esta acción también desactivará el 2FA Authenticator si está configurado.', async (e: any) => {
      if (e) {
        try {
          const response = await this.securityAdminService.deleteUserEmail(this.selectedUser!.id);
          if (response?.ok) {
            alertifyjs.success(response.msg || 'Correo electrónico eliminado exitosamente');
            this.emailForm.email = '';
            await this.loadUsers(false, true);
            this.closeManageModal();
          }
        } catch (error) {
          console.error('Error:', error);
          alertifyjs.error('Error al eliminar correo electrónico');
        }
      }
    }).set('labels', { ok: 'Aceptar', cancel: 'Cancelar' }).set('title', 'Eliminar Correo');
  };

  /**
   * Resetea 2FA (Authenticator)
   */
  reset2FAAuthenticator = async () => {
    if (!this.selectedUser) return;

    if (!confirm(`¿Estás seguro de resetear 2FA Authenticator de ${this.selectedUser.username}?`)) {
      return;
    }

    try {
      const response = await this.securityAdminService.reset2FAAuthenticator(this.selectedUser.id);
      if (response?.ok) {
        alertifyjs.success(response.msg);
        this.loadUsers(false, true);
        this.closeManageModal();
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al resetear 2FA Authenticator');
    }
  };

  reset2FASMS = async () => {
    if (!this.selectedUser) return;

    if (!confirm(`¿Estás seguro de resetear 2FA SMS de ${this.selectedUser.username}?`)) {
      return;
    }

    try {
      const response = await this.securityAdminService.reset2FASMS(this.selectedUser.id);
      if (response?.ok) {
        alertifyjs.success(response.msg);
        this.loadUsers(false, true);
        this.closeManageModal();
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al resetear 2FA SMS');
    }
  };

  setUserPhone = async () => {
    if (!this.selectedUser) return;

    if (!this.phoneForm.phone || !/^\d{10}$/.test(this.phoneForm.phone)) {
      alertifyjs.error('Número de teléfono inválido. Debe tener 10 dígitos');
      return;
    }

    try {
      const response = await this.securityAdminService.setUserPhone(this.selectedUser.id, this.phoneForm.phone);
      if (response?.ok) {
        alertifyjs.success(response.msg);
        this.loadUsers(false, true);
        this.closeManageModal();
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al actualizar número de teléfono');
    }
  };

  unlockAccount = async (user: UserSecurity) => {
    if (!confirm(`¿Desbloquear cuenta de ${user.username}?`)) {
      return;
    }

    try {
      const response = await this.securityAdminService.unlockAccount(user.id);
      if (response?.ok) {
        alertifyjs.success(response.msg);
        this.loadUsers(false, true);
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al desbloquear cuenta');
    }
  };

  resetIncidents = async (user: UserSecurity) => {
    if (!confirm(`¿Resetear incidencias de ${user.username}?`)) {
      return;
    }

    try {
      const response = await this.securityAdminService.resetIncidents(user.id);
      if (response?.ok) {
        alertifyjs.success(response.msg);
        this.loadUsers(false, true);
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al resetear incidencias');
    }
  };

  viewLoginHistory = async (user: UserSecurity) => {
    this.selectedUser = user;
    this.showLoginHistoryModal = true;
    this.loadingHistory = true;
    this.closeAllPopovers();

    try {
      const response = await this.securityAdminService.getUserLoginHistory(user.id);
      if (response?.ok) {
        this.loginHistory = response.loginHistory;
      }
      this.loadingHistory = false;
    } catch (error) {
      console.error('Error:', error);
      this.loadingHistory = false;
    }
  };

  closeLoginHistoryModal(): void {
    this.showLoginHistoryModal = false;
    this.selectedUser = null;
    this.loginHistory = [];
  }

  togglePopover(userId: string): void {
    this.openPopoverId = this.openPopoverId === userId ? null : userId;
  }

  isPopoverOpen(userId: string): boolean {
    return this.openPopoverId === userId;
  }

  closeAllPopovers(): void {
    this.openPopoverId = null;
  }

  getFullName(user: UserSecurity): string {
    return `${user.name || ''} ${user.lastName || ''}`.trim() || user.username;
  }

  formatDate(date: Date | null): string {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-VE');
  }

  getLocationString(location: any): string {
    if (!location) return 'Desconocida';
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.region) parts.push(location.region);
    if (location.country) parts.push(location.country);
    return parts.join(', ') || 'Desconocida';
  }

  getDeviceString(device: any): string {
    if (!device) return 'Desconocido';
    return `${device.browser || 'Unknown'} en ${device.os || 'Unknown'}`;
  }

  toggle2FAAuthenticator = async (active: boolean) => {
    if (!this.selectedUser) return;

    if (!this.selectedUser.email) {
      alertifyjs.error('No puedes activar 2FA Authenticator: el usuario no tiene correo electrónico asociado. Contacta al administrador.');
      return;
    }

    try {
      const response = await this.securityAdminService.toggle2FAAuthenticator(this.selectedUser.id, active);
      if (response?.ok) {
        alertifyjs.success(response.msg);
        this.loadUsers(false, true);
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al cambiar estado de 2FA Authenticator');
    }
  };

  toggle2FASMS = async (active: boolean) => {
    if (!this.selectedUser) return;

    if (!this.selectedUser.twoFactorSMS.phone) {
      alertifyjs.error('No puedes activar 2FA SMS: el usuario no tiene número de teléfono configurado.');
      return;
    }

    try {
      const response = await this.securityAdminService.toggle2FASMS(this.selectedUser.id, active);
      if (response?.ok) {
        alertifyjs.success(response.msg);
        this.loadUsers(false, true);
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al cambiar estado de 2FA SMS');
    }
  };

  loadGlobal2FAStatus = async () => {
    try {
      const response = await this.securityAdminService.getGlobal2FAStatus();
      if (response?.ok) {
        this.global2FAEnabled = response.global2FAEnabled;
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  openGlobalModal = async (): Promise<void> => {
    // Recargar el estado actual antes de abrir el modal
    await this.loadGlobal2FAStatus();
    this.showGlobalModal = true;
  }

  closeGlobalModal(): void {
    this.showGlobalModal = false;
  }

  toggleGlobal2FA = async () => {
    const newState = !this.global2FAEnabled;
    const confirmMsg = newState
      ? '¿Activar 2FA globalmente? Los usuarios con 2FA configurado deberán usarlo al iniciar sesión.'
      : '¿Desactivar 2FA globalmente? Los usuarios NO necesitarán 2FA al iniciar sesión aunque lo tengan configurado.';

    if (!confirm(confirmMsg)) {
      return;
    }

    this.loadingGlobal2FA = true;

    try {
      const response = await this.securityAdminService.toggleGlobal2FA(newState);
      if (response?.ok) {
        this.global2FAEnabled = newState;
        alertifyjs.success(response.msg);
        this.closeGlobalModal();
        // Recargar el estado después del toggle para confirmar
        await this.loadGlobal2FAStatus();
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al cambiar estado global de 2FA');
    } finally {
      this.loadingGlobal2FA = false;
    }
  };

  // === Trusted Devices ===

  openTrustedDevicesModal = async (user: UserSecurity) => {
    this.trustedDevicesUser = user;
    this.showTrustedDevicesModal = true;
    this.loadingTrustedDevices = true;
    this.closeAllPopovers();

    try {
      const response = await this.securityService.getAdminUserTrustedDevices(user.id);
      if (response?.ok) {
        this.trustedDevicesList = response.devices;
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al cargar dispositivos');
    } finally {
      this.loadingTrustedDevices = false;
    }
  };

  closeTrustedDevicesModal(): void {
    this.showTrustedDevicesModal = false;
    this.trustedDevicesUser = null;
    this.trustedDevicesList = [];
  }

  revokeTrustedDevice = async (deviceId: string) => {
    if (!this.trustedDevicesUser) return;
    if (!confirm('¿Revocar este dispositivo de confianza?')) return;

    this.revokingDeviceId = deviceId;

    try {
      const response = await this.securityService.adminRevokeTrustedDevice(this.trustedDevicesUser.id, deviceId);
      if (response?.ok) {
        alertifyjs.success(response.msg);
        // Remover de la lista local
        this.trustedDevicesList = this.trustedDevicesList.filter(d => d.deviceId !== deviceId);
        this.loadUsers(false, true);
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al revocar dispositivo');
    } finally {
      this.revokingDeviceId = null;
    }
  };

  revokeAllTrustedDevices = async () => {
    if (!this.trustedDevicesUser) return;
    if (!confirm(`¿Revocar TODOS los dispositivos de confianza de ${this.trustedDevicesUser.username}?`)) return;

    this.revokingAll = true;

    try {
      const response = await this.securityService.adminRevokeAllTrustedDevices(this.trustedDevicesUser.id);
      if (response?.ok) {
        alertifyjs.success(response.msg);
        this.trustedDevicesList = [];
        this.loadUsers(false, true);
      }
    } catch (error) {
      console.error('Error:', error);
      alertifyjs.error('Error al revocar dispositivos');
    } finally {
      this.revokingAll = false;
    }
  };
}
